import * as React from 'react'
import Link from 'next/link'

import { styled } from '@mui/material/styles'
import CardActionArea from '@mui/material/CardActionArea'
import AvatarGroup from '@mui/material/AvatarGroup'
import Avatar from '@mui/material/Avatar'
import Stack from '@mui/material/Stack'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'

import { IconName } from 'components/atomic/atoms/icon/components/custom-icons'
import { I18n, useTranslation } from 'components/hooks/use-translation'
import Text from 'components/atomic/atoms/text'
import Icon from 'components/atomic/atoms/icon'
import Chip from 'components/atomic/atoms/chip'

import { TrackOverview } from 'types/types'
import { colorsFromItemType, computeUserCount } from 'utils/paths'
import Routes from 'utils/routes'

const StyledCard = styled(Card)`
    height: 100%;
`

const CardContent = styled('div')`
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    gap: 1rem;
    justify-content: space-between;
    z-index: 1;
    top: 0;
    left: 0;
    padding: 1rem;
`

const BulletPoints = styled(Box)`
    display: flex;
    color: ${({ theme }) => theme.palette.text.secondary};
`

type CourseCardProps = TrackOverview

const CourseCard = React.memo((props: CourseCardProps) => {
    const { t }: I18n = useTranslation()

    const bulletPoints = React.useMemo((): { value: string; icon: IconName }[] => {
        return [
            {
                value: `${Math.ceil(props.time_total / 60)} ore`,
                icon: 'time',
            },
            {
                value: t(`common:difficulty.${props.difficulty}`),
                icon: props.difficulty.toLowerCase() as IconName,
            },
            {
                value: `${props.resources_count.total} Lezioni`,
                icon: 'handyman',
            },
        ]
    }, [t, props.time_total, props.difficulty, props.resources_count])

    const colors = React.useMemo(() => colorsFromItemType('course'), [])

    return (
        <Link href={Routes.Course(props.slug)}>
            <a>
                <StyledCard>
                    <CardActionArea sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" spacing={0}>
                                <Stack direction="row" spacing={1}>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={t(`common:pathGoal.course`)}
                                        sx={{ borderColor: colors[0], backgroundColor: colors[1] }}
                                    />
                                    {!props.live ? (
                                        <Chip label={'Coming Soon'} size="small" />
                                    ) : props.pro_exclusive ? (
                                        <Chip label={'Pro'} golden size="small" variant="outlined" />
                                    ) : (
                                        <Chip label={'Gratis ✨'} size="small" variant="outlined" />
                                    )}
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing="0.2rem">
                                    <AvatarGroup spacing="medium">
                                        <Avatar
                                            alt=""
                                            sx={{ width: 20, height: 20 }}
                                            src={`https://i.pravatar.cc/20?u=${props._id}1`}
                                        />
                                        <Avatar
                                            alt=""
                                            sx={{ width: 20, height: 20 }}
                                            src={`https://i.pravatar.cc/20?u=${props._id}2`}
                                        />
                                        <Avatar
                                            alt=""
                                            sx={{ width: 20, height: 20 }}
                                            src={`https://i.pravatar.cc/20?u=${props._id}3`}
                                        />
                                    </AvatarGroup>
                                    <Text sx={{ flex: 'none' }} variant="caption" color="text.secondary">
                                        +{computeUserCount(props)} Studenti
                                    </Text>
                                </Stack>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Text variant="h6" sx={{ maxWidth: '100%' }}>
                                    {props.title}
                                </Text>
                            </Stack>
                            <BulletPoints>
                                {bulletPoints.map((item) => (
                                    <Stack
                                        sx={{ flex: 'none' }}
                                        direction="row"
                                        mr={2}
                                        spacing="0.2rem"
                                        key={item.icon}>
                                        <Text
                                            variant="caption"
                                            sx={{ display: 'flex', alignItems: 'center' }}
                                            color="inherit">
                                            <Icon fontSize="inherit" sx={{ mr: 0.5 }} name={item.icon} />
                                            {item.value}
                                        </Text>
                                    </Stack>
                                ))}
                            </BulletPoints>
                        </CardContent>
                    </CardActionArea>
                </StyledCard>
            </a>
        </Link>
    )
})

export default CourseCard
